import { doc, getDoc, setDoc, collection, getDocs, query, where, deleteDoc, serverTimestamp, orderBy } from 'firebase/firestore'
import { db } from './firebase'

/**
 * Save a user profile to Firestore
 * @param {string} userId - The user's auth ID
 * @param {string} profileName - The name of the profile
 * @param {object} profileData - The profile data to save
 */
export const saveUserProfile = async (userId, profileName, profileData) => {
  try {
    const formattedProfileName = profileName.toLowerCase().replace(/\s+/g, '_')
    const profileRef = doc(db, 'users', userId, 'profiles', formattedProfileName)

    // Format the work experiences and academic achievements data
    const formattedData = {
      workExperiences: profileData.experience.entries.map((entry) => ({
        title: entry.role || '',
        company: entry.company || '',
        startDate: entry.fromDate || '',
        endDate: entry.untilDate || '',
        description: entry.content || '',
        status: entry.untilDate ? 'past' : 'current',
      })),
      academicAchievements: profileData.education.entries.map((entry) => ({
        degree: entry.course || '',
        institution: entry.university || '',
        startDate: entry.fromDate || '',
        endDate: entry.untilDate || '',
        description: entry.content || '',
      })),
      skills: profileData.skills.entries.map((entry) => entry.content || ''),
      contacts: {
        name: profileData.contacts?.name || '',
        email: profileData.contacts?.email?.map((e) => e.value) || [],
        phone: profileData.contacts?.phone?.map((p) => p.value) || [],
        location: profileData.contacts?.location || '',
        socials: profileData.contacts?.socials?.map((s) => s.value) || [],
        website: profileData.contacts?.website?.map((w) => w.value) || [],
      },
      createdAt: serverTimestamp(),
    }

    await setDoc(profileRef, formattedData)
    return { success: true, profileId: formattedProfileName }
  } catch (error) {
    console.error('Error saving profile:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get a user profile from Firestore
 * @param {string} userId - The user's auth ID
 * @param {string} profileName - The name of the profile to retrieve
 */
export const getUserProfile = async (userId, profileName) => {
  try {
    const profileRef = doc(db, 'users', userId, 'profiles', profileName)
    const profileSnapshot = await getDoc(profileRef)

    if (profileSnapshot.exists()) {
      const profileData = profileSnapshot.data()

      // Transform the data back to the format expected by the UI
      return {
        success: true,
        data: {
          experience: {
            entries: profileData.workExperiences.map((exp, index) => ({
              id: Date.now() + index,
              content: exp.description || '',
              role: exp.title || '',
              company: exp.company || '',
              fromDate: exp.startDate || '',
              untilDate: exp.endDate || '',
            })),
          },
          education: {
            entries: profileData.academicAchievements.map((edu, index) => ({
              id: Date.now() + index + 100,
              content: edu.description || '',
              course: edu.degree || '',
              university: edu.institution || '',
              fromDate: edu.startDate || '',
              untilDate: edu.endDate || '',
            })),
          },
          skills: {
            entries: profileData.skills.map((skill, index) => ({
              id: Date.now() + index + 200,
              content: skill || '',
            })),
          },
          contacts: {
            name: profileData.contacts?.name || '',
            email: (profileData.contacts?.email || []).map((value, index) => ({
              id: Date.now() + index + 300,
              value: value || '',
            })),
            phone: (profileData.contacts?.phone || []).map((value, index) => ({
              id: Date.now() + index + 400,
              value: value || '',
            })),
            location: profileData.contacts?.location || '',
            socials: (profileData.contacts?.socials || []).map((value, index) => ({
              id: Date.now() + index + 500,
              value: value || '',
            })),
            website: (profileData.contacts?.website || []).map((value, index) => ({
              id: Date.now() + index + 600,
              value: value || '',
            })),
          },
        },
      }
    } else {
      return { success: false, error: 'Profile not found' }
    }
  } catch (error) {
    console.error('Error getting profile:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get all profiles for a user from Firestore
 * @param {string} userId - The user's auth ID
 */
export const getUserProfiles = async (userId) => {
  try {
    const profilesRef = collection(db, 'users', userId, 'profiles')
    // Get profiles ordered by creation time (newest first)
    const q = query(profilesRef, orderBy('createdAt', 'desc'))
    const profilesSnapshot = await getDocs(q)

    const profiles = []
    profilesSnapshot.forEach((doc) => {
      profiles.push({
        id: doc.id,
        data: {
          contacts: doc.data().contacts || {},
          // Include other data as needed
        },
        createdAt: doc.data().createdAt,
      })
    })

    return { success: true, profiles }
  } catch (error) {
    console.error('Error getting profiles:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Delete a user profile from Firestore
 * @param {string} userId - The user's auth ID
 * @param {string} profileName - The name of the profile to delete
 */
export const deleteUserProfile = async (userId, profileName) => {
  try {
    const profileRef = doc(db, 'users', userId, 'profiles', profileName)
    await deleteDoc(profileRef)
    return { success: true }
  } catch (error) {
    console.error('Error deleting profile:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Save user information to Firestore
 * @param {string} userId - The user's auth ID
 * @param {object} userInfo - The user information to save
 */
export const saveUserInformation = async (userId, userInfo) => {
  try {
    const userInfoRef = doc(db, 'users', userId, 'user_information', 'base')
    await setDoc(userInfoRef, userInfo)
    return { success: true }
  } catch (error) {
    console.error('Error saving user information:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get user information from Firestore
 * @param {string} userId - The user's auth ID
 */
export const getUserInformation = async (userId) => {
  try {
    const userInfoRef = doc(db, 'users', userId, 'user_information', 'base')
    const userInfoSnapshot = await getDoc(userInfoRef)

    if (userInfoSnapshot.exists()) {
      return { success: true, data: userInfoSnapshot.data() }
    } else {
      return { success: false, error: 'User information not found' }
    }
  } catch (error) {
    console.error('Error getting user information:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Save user desired job role to Firestore
 * @param {string} userId - The user's auth ID
 * @param {object} jobRole - The desired job role information
 */
export const saveDesiredJobRole = async (userId, jobRole) => {
  try {
    const jobRoleRef = doc(db, 'users', userId, 'user_information', 'desired_job_role')
    await setDoc(jobRoleRef, jobRole)
    return { success: true }
  } catch (error) {
    console.error('Error saving desired job role:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get user desired job role from Firestore
 * @param {string} userId - The user's auth ID
 */
export const getDesiredJobRole = async (userId) => {
  try {
    const jobRoleRef = doc(db, 'users', userId, 'user_information', 'desired_job_role')
    const jobRoleSnapshot = await getDoc(jobRoleRef)

    if (jobRoleSnapshot.exists()) {
      return { success: true, data: jobRoleSnapshot.data() }
    } else {
      return { success: false, error: 'Desired job role not found' }
    }
  } catch (error) {
    console.error('Error getting desired job role:', error)
    return { success: false, error: error.message }
  }
}
